import type { CSSProperties, NumberInputStylesNames } from "@mantine/core";
import { Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useCommonColors } from "octalog-base";
import { useMemo } from "react";

import { arePricesDifferent } from "../../../../../common/pricing/priceUtils";
import { formatCurrency } from "../../../../../common/UnitFormatter";
import { parseDecimal } from "../../../../../common/UnitParser";
import type { NestedKeyOf } from "../../../../../common/Utils";
import type { DecimalScale2InputProps } from "../../../../../components/input/number/DecimalScale2Input";
import { PriceInput } from "../../../../../components/input/number/PriceInput";
import { QueryLoaderBoundary } from "../../../../../components/loader/QueryLoaderBoundary";
import type { PriceContainerDto } from "../../../../../generated/client/octalog-service";
import { useGetPrices } from "../../../../../service/prices/useGetPrices";
import type { OrderFormData } from "../contexts/FormContexts";
import { useOrderItems } from "../contexts/FormContexts";
import type { OrderForm } from "../hooks/useOrderForm";
import { calculateWoodPriceOrPlaceholder } from "../utils/calculateWoodPrice";

function useWoodPriceCalculation(
  priceData: PriceContainerDto,
  woodTypeId: number | undefined,
  humidityLevelId: number | undefined,
  cuttingLengthId: number | undefined,
  volume: unknown,
) {
  const volumeNumber = parseDecimal(volume);

  return useMemo(() => {
    return calculateWoodPriceOrPlaceholder({
      priceData,
      woodTypeId,
      humidityLevelId,
      cuttingLengthId,
      volume: volumeNumber,
    });
  }, [priceData, woodTypeId, humidityLevelId, cuttingLengthId, volumeNumber]);
}

interface PriceProps extends Omit<DecimalScale2InputProps, "form"> {
  priceData: PriceContainerDto;
  woodTypeId?: number;
  humidityLevelId?: number;
  cuttingLengthId?: number;
  volume?: number;
  form: OrderForm;
  formKey: NestedKeyOf<OrderFormData>;
}

function PriceCalculated({
  priceData,
  woodTypeId,
  humidityLevelId,
  cuttingLengthId,
  volume,
  form,
  formKey,
  ...rest
}: PriceProps) {
  const commonColors = useCommonColors();
  const { price, requireManualPrice, description } = useWoodPriceCalculation(
    priceData,
    woodTypeId,
    humidityLevelId,
    cuttingLengthId,
    volume,
  );

  const style: Partial<Record<NumberInputStylesNames, CSSProperties>> = requireManualPrice
    ? { input: { textAlign: "center" } }
    : { input: { textAlign: "center", background: "transparent", color: commonColors.text, opacity: 1 } };

  return (
    <PriceInput
      {...form.getInputProps(formKey)}
      {...rest}
      description={description}
      disabled={!requireManualPrice}
      externalValue={price}
      styles={style}
    />
  );
}

function PricePreset({
  priceData,
  woodTypeId,
  humidityLevelId,
  cuttingLengthId,
  volume,
  form,
  formKey,
  ...rest
}: PriceProps) {
  const { price, requireManualPrice, description } = useWoodPriceCalculation(
    priceData,
    woodTypeId,
    humidityLevelId,
    cuttingLengthId,
    volume,
  );

  const style: Partial<Record<NumberInputStylesNames, CSSProperties>> = { input: { textAlign: "center" } };

  const currentValueRaw = form.getInputProps(formKey).value as unknown;
  const valuesDiffer = arePricesDifferent(currentValueRaw, price);

  const alert =
    (typeof price === "number" && !requireManualPrice && valuesDiffer && (
      <Alert color="yellow" icon={<IconInfoCircle />} title="Preisabweichung" variant="light">
        Der gespeicherte Preis weicht vom automatisch berechneten Preis ab.
        <br />
        <br />
        Automatisch berechneter Preis: <strong>{formatCurrency(price)}</strong>
        <br />
        Berechnungsgrundlage: <strong>{description}</strong>
      </Alert>
    )) ||
    null;

  const descriptionProps = alert ? { component: "div" } : undefined;

  return (
    <PriceInput
      {...form.getInputProps(formKey)}
      {...rest}
      description={alert ?? description}
      descriptionProps={descriptionProps}
      styles={style}
    />
  );
}

interface CalculatedPriceProps {
  woodTypeId?: number;
  humidityLevelId?: number;
  cuttingLengthId?: number;
  volume?: number;
  formKey: NestedKeyOf<OrderFormData>;
  usePreset?: boolean;
}

export function CalculatedWoodPrice({
  woodTypeId,
  humidityLevelId,
  cuttingLengthId,
  volume,
  formKey,
  usePreset = false,
}: CalculatedPriceProps) {
  const { form } = useOrderItems();
  const priceData = useGetPrices();

  function renderPrice(priceData: PriceContainerDto) {
    return usePreset ? (
      <PricePreset
        cuttingLengthId={cuttingLengthId}
        form={form}
        formKey={formKey}
        humidityLevelId={humidityLevelId}
        priceData={priceData}
        volume={volume}
        woodTypeId={woodTypeId}
      />
    ) : (
      <PriceCalculated
        cuttingLengthId={cuttingLengthId}
        form={form}
        formKey={formKey}
        humidityLevelId={humidityLevelId}
        priceData={priceData}
        volume={volume}
        woodTypeId={woodTypeId}
      />
    );
  }

  return <QueryLoaderBoundary queryResult={priceData}>{(data) => renderPrice(data)}</QueryLoaderBoundary>;
}
